/**
 * Proactive JWT refresh.
 *
 * Before: the first 401 after the token expired wiped the session and
 * bounced the user to /login mid-task. Now `api()` calls
 * `ensureFreshToken()` before every authenticated request; when the token
 * expires within REFRESH_THRESHOLD_SEC it is exchanged for a new one via
 * POST /api/auth/refresh (single-flight — concurrent callers share one
 * request). The 401 path in api.ts stays as the fallback for revoked or
 * already-expired tokens.
 */
import { getToken, getTokenExpiry, useAuth, type AuthUser } from "./auth";

export const REFRESH_THRESHOLD_SEC = 10 * 60;

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export function shouldRefreshToken(token: string | null, nowMs: number = Date.now()): boolean {
  const exp = getTokenExpiry(token);
  if (exp == null) return false;
  const secondsLeft = exp - nowMs / 1000;
  // Already expired → the request will 401 and api.ts handles logout;
  // refreshing with a dead token would 401 too.
  if (secondsLeft <= 0) return false;
  return secondsLeft < REFRESH_THRESHOLD_SEC;
}

let inflight: Promise<string | null> | null = null;

async function doRefresh(current: string): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: "POST",
      headers: { Authorization: `Bearer ${current}` },
    });
    if (!res.ok) return current;
    const data = (await res.json()) as { token?: string; user?: AuthUser };
    if (!data?.token) return current;
    const user = data.user ?? useAuth.getState().user;
    if (user) useAuth.getState().setSession(data.token, user);
    return data.token;
  } catch {
    return current;
  }
}

/**
 * Returns the token to use for the next request — refreshed if it was about
 * to expire, unchanged otherwise (or when the refresh call fails).
 */
export async function ensureFreshToken(): Promise<string | null> {
  const current = getToken();
  if (!current) return null;
  if (!shouldRefreshToken(current)) return current;
  if (!inflight) {
    inflight = doRefresh(current).finally(() => {
      inflight = null;
    });
  }
  return inflight;
}
